<template>
    <article
        class="scc-subgames-rank"
        :class="
            `scc-subgames-rank--${gridLayout} scc-subgames-rank--${hasShadow} scc-subgames-rank--${backgroundColor} ${
                hasPadding ? 'scc-subgames-rank--has-padding' : ''
            }`
        "
    >
        <div>
            <h2 class="scc-subgames-rank__subgames-title">{{ title }}</h2>
            <p class="scc-subgames-rank__subgames-description">{{ paragraph }}</p>
            <SccLink v-if="linkText" :to="link" class="scc-subgames-rank__subgames-link">
                {{ linkText }}
            </SccLink>
        </div>
        <div v-if="showSearchbar" class="scc-subgames-rank__search">
            <span class="scc-subgames-rank__search-label">
                Zoek je club
            </span>
            <form class="scc-subgames-rank__form" @submit.prevent="searchClub()">
                <input
                    ref="input"
                    v-model="searchTerm"
                    class="scc-subgames-rank__search-input"
                    type="text"
                    placeholder="Clubnaam"
                    @focus="setFocus(true)"
                    @blur="setFocus(false)"
                />
                <div class="scc-subgames-rank__search-button" @click="searchClub()">
                    <magnify-icon />
                </div>
            </form>
        </div>
        <div v-if="results.length > 0 && activeSearchTerm" class="scc-subgames-rank__result">
            <p>
                <b>
                    <i>Je hebt gezocht op '{{ activeSearchTerm }}'</i>
                </b>
            </p>
            <template v-for="(subgame, index) in results">
                <SubgameRankBlock
                    :key="index"
                    :rank="subgame.rank"
                    :name="subgame.name"
                    :amount="subgame.ticketCount"
                    :slug="subgame.slug"
                    :path="subgame.path"
                    :showLink="showSubgameLink"
                    active
                />
            </template>
        </div>
        <div :class="`${hasColumns ? 'scc-subgames-rank__container--columns' : ''}`">
            <Spinner v-if="loading" />
            <template v-else-if="subGamesList.length > 0">
                <template v-for="(subgame, index) in subGamesList">
                    <SubgameRankBlock
                        :key="index"
                        :rank="index + 1"
                        :name="subgame.name"
                        :amount="subgame.ticketCount"
                        :slug="subgame.slug"
                        :path="subgame.path"
                        :showLink="showSubgameLink"
                        :hasColumns="hasColumns"
                        :keepLeft="keepLeft(index, subGamesList)"
                        :showCurrentClub="showCurrentClub"
                        :isMain="isMain(subgame.name)"
                    />
                </template>
            </template>
            <template v-else>
                <p class="scc-subgames-rank__fallback">Geen resultaat gevonden</p>
            </template>
        </div>
        <p class="scc-subgames-rank__subgames-link-sentence">
            {{ formattedlink.firstSection }}
            <SccLink :to="link" class="scc-subgames-rank__subgames-overview-link">
                {{ formattedlink.markerSection }}
            </SccLink>
            {{ formattedlink.lastSection }}
        </p>
    </article>
</template>

<script>
import './subgame-rank.scss';
import magnifyIcon from '../../assets/icons/magnify-icon.vue';
import SccLink from '../global/SccLink';
import SubgameRankBlock from './SubgameRankBlock';
import Spinner from '../global/Spinner';

const colors = ['primary', 'secondary', 'white', 'grey'];

export default {
    name: 'SubgameRank',
    components: { Spinner, SubgameRankBlock, magnifyIcon, SccLink },
    __editor: {
        requiredData: ['game'],
        fields: {
            backgroundColor: {
                widget: 'select',
                values: colors,
            },
            gridLayout: {
                widget: 'select',
                values: ['span-1', 'span-2', 'span-3'],
            },
            hasShadow: {
                widget: 'select',
                values: ['has-shadow', 'no-shadow'],
            },
            hasPadding: {
                widget: 'checkbox',
            },
            title: {
                widget: 'text',
            },
            paragraph: {
                widget: 'textarea',
            },
            showSearchbar: {
                widget: 'checkbox',
            },
            showSubgameLink: {
                widget: 'checkbox',
            },
            linkText: {
                widget: 'text',
            },
            link: {
                widget: 'text',
            },
            linkSentence: {
                widget: 'text',
            },
            linkSentenceMarker: {
                widget: 'text',
            },
        },
    },
    props: {
        game: {
            type: Object,
            required: true,
        },
        backgroundColor: {
            type: String,
            required: false,
            default: 'primary',
            validator: function(val) {
                return colors.includes(val);
            },
        },
        gridLayout: {
            type: String,
            default: 'span-2',
        },
        hasShadow: {
            type: String,
            default: 'no-shadow',
        },
        hasPadding: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: 'Subgame title',
        },
        paragraph: {
            type: String,
            default: '',
        },
        showSearchbar: {
            type: Boolean,
            default: false,
        },
        showSubgameLink: {
            type: Boolean,
            default: false,
        },
        limit: {
            type: Number,
            default: 5,
        },
        linkText: {
            type: String,
            default: '',
        },
        link: {
            type: String,
            default: '',
        },
        linkSentence: {
            type: String,
            default: '',
        },
        linkSentenceMarker: {
            type: String,
            default: '',
        },
        hasColumns: {
            type: Boolean,
            default: false,
        },
        showCurrentClub: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: true,
            searchTerm: '',
            activeSearchTerm: '',
            slug: undefined,
            path: '',
            focussed: false,
            subGamesList: [],
            results: [],
        };
    },
    computed: {
        formattedlink() {
            if (!this.linkSentenceMarker) return { firstSection: this.linkSentence };

            if (!this.linkSentence.includes(this.linkSentenceMarker)) return 'Marker is not contained in sentence';

            const sentenceSections = this.linkSentence.split(this.linkSentenceMarker);

            if (sentenceSections.length > 2) return 'Marker is mentioned twice in sentence';

            return {
                firstSection: sentenceSections[0],
                markerSection: ' ' + this.linkSentenceMarker,
                lastSection: sentenceSections[1],
            };
        },
    },
    created() {
        this.$api
            ?.getSubGames({
                gameId: this.game.id,
                filters: [{ field: 'status', value: 'active', operator: 'equal' }],
                sorting: [{ field: 'ticket_count', direction: 'desc' }],
                pagination: { size: this.limit, number: 1 },
            })
            .then(subGames => (this.subGamesList = subGames || []))
            .then((this.loading = false));
    },
    methods: {
        searchClub() {
            this.activeSearchTerm = this.searchTerm;
            this.$api
                ?.getSubGames({
                    gameId: this.game.id,
                    sorting: [{ field: 'ticket_count', direction: 'desc' }],
                    pagination: { size: 5, number: 1 },
                    filters: [
                        { field: 'status', value: 'active', operator: 'equal' },
                        { field: 'search_wildcard', value: `%${this.activeSearchTerm}%` },
                    ],
                })
                .then(subGames => (this.results = subGames));
        },
        setFocus(bool) {
            const state = this;
            setTimeout(() => {
                state.focussed = bool;
            }, 200);
        },
        keepLeft(index, list) {
            return index < list.length / 2;
        },
        isMain(name) {
            if (!this.showCurrentClub) return false;

            return name === this.game.name;
        },
    },
};
</script>
